import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import useProfile from '../hooks/useProfile';
import { SocialLink } from '../services/api';

const ContactPage: React.FC = () => {
  const { profile, loading, error } = useProfile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<{
    submitting: boolean;
    success: boolean;
    error: string | null;
  }>({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus(prev => ({ ...prev, submitting: true, error: null }));
    
    // In a real app, we would send this data to a server endpoint
    // For now, we'll just simulate a successful submission
    setTimeout(() => {
      setFormStatus({
        submitting: false,
        success: true,
        error: null
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  const getPlatformIcon = (platform: string) => {
    const lowerPlatform = platform.toLowerCase();
    
    switch (lowerPlatform) {
      case 'instagram':
        return '📸';
      case 'facebook':
        return '👤';
      case 'twitter':
        return '🐦';
      case 'linkedin':
        return '💼';
      case 'pinterest':
        return '📌';
      case 'youtube':
        return '📹';
      case 'tiktok':
        return '🎵';
      case 'behance':
        return '🎨';
      case 'dribbble':
        return '🏀';
      case 'artstation':
        return '🖼️';
      case 'deviantart':
        return '🎭';
      case 'website':
        return '🌐';
      default:
        return '🔗';
    }
  };

  const SocialLinks: React.FC<{ links: SocialLink[] }> = ({ links }) => {
    if (links.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-3 mt-6">
        {links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            aria-label={`Visit my ${link.platform} page`}
          >
            <span>{getPlatformIcon(link.platform)}</span>
            <span>{link.platform}</span>
          </a>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="bg-light">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-black mb-12 text-black">Get in Touch</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="bg-white p-8 border-2 border-black mb-8">
                <h2 className="text-2xl font-bold mb-8 text-black">Contact Information</h2>
                
                {loading ? (
                  <div className="py-4">
                    <div className="animate-pulse h-5 bg-gray-200 rounded w-40 mb-4"></div>
                    <div className="animate-pulse h-5 bg-gray-200 rounded w-64 mb-4"></div>
                    <div className="animate-pulse h-5 bg-gray-200 rounded w-52"></div>
                  </div>
                ) : error ? (
                  <div className="text-red-500">{error}</div>
                ) : profile?.contactInfo ? (
                  <div className="space-y-6">
                    {profile.contactInfo.email && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center border-2 border-black text-black">
                          ✉️
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-semibold text-gray-600">Email</p>
                          <a
                            href={`mailto:${profile.contactInfo.email}`}
                            className="text-lg text-black hover:opacity-70 underline font-semibold"
                          >
                            {profile.contactInfo.email}
                          </a>
                        </div>
                      </div>
                    )}

                    {profile.contactInfo.phone && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center border-2 border-black text-black">
                          📞
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-semibold text-gray-600">Phone</p>
                          <a
                            href={`tel:${profile.contactInfo.phone}`}
                            className="text-lg text-black hover:opacity-70 underline font-semibold"
                          >
                            {profile.contactInfo.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {profile.contactInfo.address && (
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center border-2 border-black text-black">
                          📍
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-semibold text-gray-600">Address</p>
                          <p className="text-lg text-gray-900">{profile.contactInfo.address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No contact information has been added yet.</p>
                )}
                
                <SocialLinks links={profile?.socialLinks || []} />
              </div>
              
              <div className="bg-white p-8 border-2 border-black">
                <h2 className="text-2xl font-bold mb-6 text-black">Business Hours</h2>
                <div className="space-y-2">
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="font-medium">Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="font-medium">Saturday</span>
                    <span>10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="font-medium">Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-500">
                  Hours may vary for on-site mural painting projects.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-8 border-2 border-black">
              <h2 className="text-2xl font-bold mb-8 text-black">Send a Message</h2>

              {formStatus.success ? (
                <div className="text-center py-10">
                  <div className="text-black text-5xl mb-4">✓</div>
                  <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                  <p className="text-gray-600 mb-6">
                    Your message has been sent successfully. I'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => setFormStatus(prev => ({ ...prev, success: false }))}
                    className="inline-block px-6 py-3 bg-black text-white font-semibold rounded hover:bg-gray-800 transition-all"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-black mb-2">
                      Your Name <span className="text-black">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-3 border-2 border-gray-300 rounded focus:outline-none focus:border-black"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                      Your Email <span className="text-black">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 border-2 border-gray-300 rounded focus:outline-none focus:border-black"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-black mb-2">
                      Subject <span className="text-black">*</span>
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full p-3 border-2 border-gray-300 rounded focus:outline-none focus:border-black"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-black mb-2">
                      Message <span className="text-black">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full p-3 border-2 border-gray-300 rounded focus:outline-none focus:border-black"
                    />
                  </div>

                  {formStatus.error && (
                    <div className="text-black text-sm border-2 border-black px-4 py-3">
                      {formStatus.error}
                    </div>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={formStatus.submitting}
                      className="w-full py-4 px-6 bg-black text-white font-semibold rounded hover:bg-gray-800 transition-all disabled:opacity-50"
                    >
                      {formStatus.submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;